import { useState, useEffect, useCallback } from 'react';
import { Pin, PinOff, MoreVertical, Bell, BellOff, Bookmark } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ChatChannelResponse, ChatChannelListResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { seededGradient, channelDisplayName } from '../utils';
import { usePressAndHold } from '../hooks/usePressAndHold';

const MESSAGE_PREVIEW_MAX_LENGTH = 50;

function stripHtmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() ?? '';
}

interface ChannelListItemProps {
  channel: ChatChannelResponse;
  isActive: boolean;
  onClick: () => void;
  /** Press-and-hold opens a read-only preview of this channel (no mark-as-read). */
  onPreview?: (channel: ChatChannelResponse) => void;
  onPinToggled?: (channelId: string, isPinned: boolean) => void;
  onPinFailed?: (channelId: string) => void;
}

export function ChannelListItem({
  channel,
  isActive,
  onClick,
  onPreview,
  onPinToggled,
  onPinFailed,
}: ChannelListItemProps) {
  const isSelfDm = channel.isSelfDm === true;
  const displayName = channelDisplayName(channel);

  // Press-and-hold (mouse or touch) peeks at the channel without opening it —
  // and without marking it read. The hook swallows the trailing click so the
  // hold never also fires the select handler below.
  const pressHandlers = usePressAndHold(() => onPreview?.(channel));

  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(
    channel.imageRef ?? null,
  );

  useEffect(() => {
    setResolvedAvatarUrl(channel.imageRef ?? null);
    if (!channel.imageRef && channel.avatarFileId) {
      api
        .getDownloadUrl(channel.avatarFileId)
        .then((r) => setResolvedAvatarUrl(r.downloadUrl))
        .catch(() => {});
    }
  }, [channel.channelId, channel.imageRef, channel.avatarFileId]);

  const lastMessagePreview = channel.lastMessage
    ? (() => {
        const plain = stripHtmlToPlainText(channel.lastMessage.content);
        const text = plain || 'File sent';
        return text.length > MESSAGE_PREVIEW_MAX_LENGTH
          ? text.substring(0, MESSAGE_PREVIEW_MAX_LENGTH) + '...'
          : text;
      })()
    : null;

  const lastMessageTime = channel.lastMessage
    ? formatRelativeTime(channel.lastMessage.createdAt)
    : null;

  const unread = channel.unreadCount ?? 0;
  const mentionCount = channel.mentionCount ?? 0;
  const isMuted = channel.isMuted ?? false;
  const initial = displayName.charAt(0).toUpperCase() || '?';
  const hasLeft = channel.hasLeft ?? false;
  const isPinned = channel.isPinned ?? false;

  const handlePinToggle = useCallback(
    async (_e: Event) => {
      const newPinState = !isPinned;
      onPinToggled?.(channel.channelId, newPinState);
      try {
        if (isPinned) {
          await api.unpinChatChannel(channel.channelId);
        } else {
          await api.pinChatChannel(channel.channelId);
        }
      } catch {
        onPinFailed?.(channel.channelId);
      }
    },
    [channel.channelId, isPinned, onPinToggled, onPinFailed],
  );

  const queryClient = useQueryClient();
  const handleMuteToggle = useCallback(async () => {
    const next = !isMuted;
    // Optimistically flip isMuted in the channels cache so the muted glyph and
    // the notification path (which reads this cache) update immediately — same
    // approach as ChannelSettingsModal's mute toggle.
    const apply = (val: boolean) =>
      queryClient.setQueryData<ChatChannelListResult>(queryKeys.chat.channels(), (old) =>
        old?.channels
          ? {
              ...old,
              channels: old.channels.map((c) =>
                c.channelId === channel.channelId ? { ...c, isMuted: val } : c,
              ),
            }
          : old,
      );
    apply(next);
    try {
      if (next) await api.muteChatChannel(channel.channelId);
      else await api.unmuteChatChannel(channel.channelId);
      // Refresh the list and the cross-arche worktop/header badge, which now
      // excludes (mute) or re-includes (unmute) this channel's unread count.
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.summary() });
    } catch {
      apply(isMuted); // revert on failure
    }
  }, [channel.channelId, isMuted, queryClient]);

  return (
    <div
      className={cn(
        'relative rounded-lg w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors group',
        isActive ? 'bg-[#111827] text-[#FFF]' : 'hover:bg-foreground/10',
        hasLeft && 'opacity-60',
      )}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <button
        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer select-none"
        onClick={onClick}
        {...(onPreview ? pressHandlers : {})}
      >
        <div
          className="h-10 w-10 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
          style={
            !resolvedAvatarUrl || isSelfDm ? { background: seededGradient(displayName) } : undefined
          }
        >
          {isSelfDm ? (
            <Bookmark className="h-5 w-5 text-white" fill="currentColor" />
          ) : resolvedAvatarUrl ? (
            <img src={resolvedAvatarUrl} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-medium text-white">{initial}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={cn(
                  'text-sm font-medium truncate',
                  isActive ? 'text-[#FFF]' : 'text-foreground',
                )}
              >
                {displayName}
              </span>
              {isPinned && (
                <Pin
                  className={cn(
                    'flex-shrink-0 h-3 w-3 rotate-45',
                    isActive ? 'text-[#FFF]/70' : 'text-muted-foreground',
                  )}
                />
              )}
              {hasLeft && (
                <span className="flex-shrink-0 text-[10px] font-medium px-1 py-0.5 rounded bg-muted text-muted-foreground leading-none">
                  Left
                </span>
              )}
              {isMuted && (
                <BellOff
                  aria-label="muted"
                  className={cn(
                    'flex-shrink-0 h-3 w-3',
                    isActive ? 'text-[#FFF]/70' : 'text-muted-foreground',
                  )}
                />
              )}
            </div>
            {lastMessageTime && (
              <span
                className={cn(
                  'text-xs flex-shrink-0 ml-2',
                  isActive ? 'text-[#FFF]' : 'text-muted-foreground',
                )}
              >
                {lastMessageTime}
              </span>
            )}
          </div>
          {(lastMessagePreview || unread > 0 || mentionCount > 0) && (
            <div className="flex items-left gap-1 mt-0.5">
              {lastMessagePreview && (
                <p
                  className={cn(
                    'text-xs truncate flex-1 align-left text-left',
                    isActive ? 'text-[#FFF]' : 'text-muted-foreground',
                  )}
                >
                  {lastMessagePreview}
                </p>
              )}
              {mentionCount > 0 && (
                <span
                  aria-label="mentioned"
                  className={cn(
                    'text-[10px] font-bold rounded-full w-[18px] h-[18px] flex items-center justify-center flex-shrink-0',
                    isActive ? 'bg-background text-foreground' : 'bg-red-500 text-white',
                  )}
                >
                  @
                </span>
              )}
              {unread > 0 && (
                <span
                  className={cn(
                    'text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center flex-shrink-0',
                    isActive ? 'bg-background text-foreground' : 'bg-red-500 text-white',
                  )}
                >
                  {unread}
                </span>
              )}
            </div>
          )}
        </div>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label={`Options for ${displayName}`}
            className={cn(
              'flex-shrink-0 h-6 w-6 rounded flex items-center justify-center transition-opacity',
              'opacity-0 group-hover:opacity-100 focus:opacity-100',
              isActive
                ? 'hover:bg-white/10 text-[#FFF]'
                : 'hover:bg-foreground/10 text-muted-foreground',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onSelect={handlePinToggle}>
            {isPinned ? (
              <>
                <PinOff className="h-4 w-4 mr-2" />
                Unpin from top
              </>
            ) : (
              <>
                <Pin className="h-4 w-4 mr-2" />
                Pin to top
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void handleMuteToggle()}>
            {isMuted ? (
              <>
                <Bell className="h-4 w-4 mr-2" />
                Unmute
              </>
            ) : (
              <>
                <BellOff className="h-4 w-4 mr-2" />
                Mute
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
